import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Row,
  Col,
  Spinner,
  Alert,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import { useGetInventoryImageAttachmentsQuery } from "slices/InventoryManagement/inventoryManagementSlice";
import { skipToken } from '@reduxjs/toolkit/query';

interface ProductImageSelectionModalProps {
  isOpen: boolean;
  toggle: () => void;
  onSelect: (image: {
    id: number;
    name: string;
    file_url: string;
    file: string;
  }) => void;
  selectedImageId?: number;
  isLoading?: boolean;
  // New props for pagination and search
  currentPage?: number;
  pageSize?: number;
  searchTerm?: string;
  onPageChange?: (page: number) => void;
  onSearchChange?: (search: string) => void;
  onClearSearch?: () => void;
}

interface ImageItem {
  id: number;
  name: string;
  file_url: string;
  file: string;
  description?: string | null;
  products: number[];
  created_at: string;
  modified_at: string;
}

const ProductImageSelectionModal: React.FC<ProductImageSelectionModalProps> = ({
  isOpen,
  toggle,
  onSelect,
  selectedImageId,
  isLoading = false,
  // New props with defaults
  currentPage = 1,
  pageSize = 12,
  searchTerm = "",
  onPageChange,
  onSearchChange,
  onClearSearch,
}) => {
  const [searchInputValue, setSearchInputValue] = useState("");
  const [localSelectedImageId, setLocalSelectedImageId] = useState<number | undefined>(selectedImageId);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout>();

  // Initialize search input value when modal opens or searchTerm changes
  useEffect(() => {
    if (isOpen) {
      setSearchInputValue(searchTerm);
      // Focus search input when modal opens
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);
    }
  }, [isOpen, searchTerm]);

  // Update local selected image when prop changes
  useEffect(() => {
    setLocalSelectedImageId(selectedImageId);
  }, [selectedImageId]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Debug logs
  useEffect(() => {
    console.log('Modal state:', {
      isOpen,
      currentPage,
      pageSize,
      searchTerm,
      searchInputValue,
      hasOnSearchChange: !!onSearchChange,
      hasOnPageChange: !!onPageChange
    });
  }, [isOpen, currentPage, pageSize, searchTerm, searchInputValue]);

  // Query with search parameter - IMPORTANT: Only query when modal is open
  const {
    data: imagesData,
    isLoading: imagesLoading,
    error,
    refetch,
  } = useGetInventoryImageAttachmentsQuery(
    isOpen ? {
      page: currentPage,
      pageSize: pageSize,
      search: searchTerm,
    } : skipToken,
    {
      skip: !isOpen, // Skip query when modal is closed
      refetchOnMountOrArgChange: true,
    }
  );

  const filteredImages = imagesData?.results || [];
  const totalPages = imagesData?.total_page || 1;
  const totalCount = imagesData?.count || 0;

  const handlePageChange = (newPage: number) => {
    console.log('Page change to:', newPage);
    if (newPage >= 1 && newPage <= totalPages) {
      if (onPageChange) {
        onPageChange(newPage);
      }
    }
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    console.log('Search input changed:', value);
    setSearchInputValue(value);
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Use immediate update with small delay
    searchTimeoutRef.current = setTimeout(() => {
      console.log('Calling onSearchChange with:', value);
      if (onSearchChange) {
        onSearchChange(value);
        // Reset to page 1 when searching
        if (onPageChange) {
          console.log('Resetting to page 1');
          onPageChange(1);
        }
      }
    }, 300);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Search submitted:', searchInputValue);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    if (onSearchChange) {
      console.log('Immediate search change call');
      onSearchChange(searchInputValue);
      if (onPageChange) {
        console.log('Setting page to 1');
        onPageChange(1);
      }
    }
  };

  const handleClearSearch = () => {
    console.log('Clearing search');
    setSearchInputValue("");
    if (onClearSearch) {
      onClearSearch();
    } else if (onSearchChange) {
      onSearchChange("");
    }
    if (onPageChange) {
      onPageChange(1);
    }
  };

  const handleImageSelect = (image: ImageItem) => {
    console.log('Image selected:', image.id);
    setLocalSelectedImageId(image.id);
  };

  const handleConfirmSelection = () => {
    console.log('Confirming selection:', localSelectedImageId);
    const selectedImage = filteredImages.find((img: ImageItem) => img.id === localSelectedImageId);
    if (selectedImage) {
      onSelect({
        id: selectedImage.id,
        name: selectedImage.name,
        file_url: selectedImage.file_url,
        file: selectedImage.file,
      });
    }
  };

  const handleToggle = () => {
    console.log('Closing modal');
    // Clear timeout when closing
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleToggle} size="lg">
      <ModalHeader toggle={handleToggle}>
        <div className="d-flex align-items-center">
          <i className="ri-image-line me-2"></i>
          Select Product Image
        </div>
      </ModalHeader>
      <ModalBody>
        {/* Search Form */}
        <form onSubmit={handleSearchSubmit} className="mb-3">
          <InputGroup>
            <InputGroupText>
              <i className="ri-search-line"></i>
            </InputGroupText>
            <Input
              innerRef={searchInputRef}
              type="text"
              placeholder="Search images by name..."
              value={searchInputValue}
              onChange={handleSearchInputChange}
              disabled={imagesLoading || isLoading}
            />
            {searchInputValue && (
              <Button
                type="button"
                color="light"
                onClick={handleClearSearch}
                disabled={imagesLoading || isLoading}
              >
                <i className="ri-close-line"></i>
              </Button>
            )}
            <Button
              type="submit"
              color="primary"
              disabled={imagesLoading || isLoading}
            >
              Search
            </Button>
          </InputGroup>
          {searchTerm && (
            <div className="mt-2">
              <small className="text-muted">
                Searching for: <strong>"{searchTerm}"</strong>
                {imagesLoading && <span className="ms-2"><Spinner size="sm" /> Loading...</span>}
              </small>
            </div>
          )}
        </form>

        {/* Error State */}
        {error && (
          <Alert color="danger" className="mb-3">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <i className="ri-error-warning-line me-2"></i>
                Failed to load images
              </div>
              <Button 
                color="link" 
                size="sm" 
                onClick={() => refetch()}
                className="p-0 text-decoration-none"
              >
                <i className="ri-refresh-line me-1"></i>
                Retry
              </Button>
            </div>
          </Alert>
        )}

        {/* Loading State */}
        {imagesLoading ? (
          <div className="text-center py-5">
            <Spinner color="primary" className="mb-3" />
            <p className="text-muted">Loading images...</p>
          </div>
        ) : (
          <>
            {/* Images Grid */}
            <Row>
              {filteredImages.length > 0 ? (
                filteredImages.map((image: ImageItem) => (
                  <Col lg={3} md={4} sm={6} key={image.id} className="mb-3">
                    <div
                      className={`image-select-card position-relative ${
                        localSelectedImageId === image.id ? "selected" : ""
                      } ${isLoading ? "disabled" : ""}`}
                      onClick={() => !isLoading && handleImageSelect(image)}
                      style={{
                        cursor: isLoading ? "not-allowed" : "pointer",
                        opacity: isLoading ? 0.6 : 1,
                        border: localSelectedImageId === image.id 
                          ? "2px solid #405189" 
                          : "1px solid #dee2e6",
                        borderRadius: "8px",
                        padding: "10px",
                        height: "100%",
                        transition: "all 0.2s ease",
                        backgroundColor: localSelectedImageId === image.id 
                          ? "rgba(64, 81, 137, 0.05)" 
                          : "white",
                      }}
                    >
                      {/* Selected Indicator */}
                      {localSelectedImageId === image.id && (
                        <div
                          className="position-absolute top-0 end-0 m-2 bg-primary rounded-circle d-flex align-items-center justify-content-center"
                          style={{
                            width: "24px",
                            height: "24px",
                          }}
                        >
                          <i className="ri-check-line text-white" style={{ fontSize: "12px" }}></i>
                        </div>
                      )}

                      {/* Image Preview */}
                      <div className="text-center mb-2" style={{ minHeight: "80px" }}>
                        <img
                          src={image.file_url}
                          alt={image.name}
                          className="img-fluid"
                          style={{
                            maxHeight: "80px",
                            width: "auto",
                            objectFit: "contain",
                            borderRadius: "4px",
                          }}
                          onError={(e) => {
                            e.currentTarget.src = "/images/default-image.png";
                          }}
                        />
                      </div>

                      {/* Image Name */}
                      <div
                        className="text-center"
                        style={{
                          fontSize: "12px",
                          fontWeight: "500",
                          lineHeight: "1.4",
                          wordBreak: "break-word",
                        }}
                      >
                        {image.name}
                      </div>

                      {/* Product Count */}
                      {image.products && image.products.length > 0 && (
                        <div
                          className="text-center mt-1"
                          style={{
                            fontSize: "10px",
                            color: "#6c757d",
                          }}
                        >
                          <i className="ri-product-hunt-line me-1"></i>
                          Used in {image.products.length} product{image.products.length !== 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                  </Col>
                ))
              ) : (
                <Col>
                  <div className="text-center py-5">
                    <i className="ri-image-line display-4 text-muted mb-3"></i>
                    <h6 className="text-muted">
                      {searchTerm
                        ? `No images found matching "${searchTerm}"`
                        : "No images available"}
                    </h6>
                    {searchTerm && (
                      <Button 
                        color="link" 
                        size="sm" 
                        onClick={handleClearSearch}
                        className="mt-2"
                      >
                        Clear search to see all images
                      </Button>
                    )}
                  </div>
                </Col>
              )}
            </Row>

            {/* Pagination */}
            {totalCount > 0 && (
              <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
                <div>
                  <span className="text-muted">
                    Showing {((currentPage - 1) * pageSize) + 1} to{" "}
                    {Math.min(currentPage * pageSize, totalCount)} of{" "}
                    {totalCount} images
                  </span>
                </div>
                <div className="d-flex gap-2 align-items-center">
                  <Button
                    color="light"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1 || imagesLoading}
                  >
                    <i className="ri-arrow-left-line"></i>
                  </Button>
                  <span className="mx-2">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    color="light"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages || imagesLoading}
                  >
                    <i className="ri-arrow-right-line"></i>
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <div className="d-flex justify-content-between align-items-center w-100">
          <div>
            {localSelectedImageId && (
              <span className="text-success">
                <i className="ri-checkbox-circle-fill me-1"></i>
                Image selected
              </span>
            )}
          </div>
          <div className="d-flex gap-2">
            <Button
              color="secondary"
              onClick={handleToggle}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={handleConfirmSelection}
              disabled={!localSelectedImageId || isLoading}
            >
              {isLoading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Processing...
                </>
              ) : (
                <>
                  <i className="ri-check-line me-1"></i>
                  Select Image
                </>
              )}
            </Button>
          </div>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default ProductImageSelectionModal;